import React, { useEffect, useState } from "react";
import NavBar from "../shared/components/Navbar";
import Footer from "../shared/components/Footer";

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPostUrl, setSelectedPostUrl] = useState(null);
  const [selectedPostTitle, setSelectedPostTitle] = useState('');

  const WP_API_URL = 'https://mycashcanvas.com/wp/wp-json/wp/v2/posts';

  useEffect(() => {
    setLoading(true);
    fetch(`${WP_API_URL}?per_page=20&_embed`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`Failed to fetch posts: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setPosts(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching posts:', err);
        setError(err.message || 'Failed to load posts');
        setLoading(false);
      });
  }, []);

  // Show actual WordPress post in iframe when selected
  if (selectedPostUrl) {
    return (
      <div className="min-h-screen flex flex-col bg-background-main">
        <NavBar />
        <main className="flex-1 max-w-6xl mx-auto pt-28 pb-10 px-4 w-full">
          <div className="flex items-center gap-3 mb-6">
            <button 
              onClick={() => setSelectedPostUrl(null)} 
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded transition-colors font-medium"
            >
              ← Back to Blog
            </button>
            <h2 className="text-lg font-semibold flex-1 line-clamp-2">{selectedPostTitle}</h2>
            <a 
              href={selectedPostUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="px-4 py-2 bg-btn-primary text-white rounded hover:bg-opacity-90 transition-colors font-medium whitespace-nowrap"
            >
              Open on WordPress ↗
            </a>
          </div>
          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <iframe 
              src={selectedPostUrl}
              title={selectedPostTitle}
              className="w-full h-full border-0"
              style={{ minHeight: '800px' }}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
            />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-main">
      <NavBar />
      <main className="flex-1 max-w-5xl mx-auto pt-28 pb-10 px-4">
        <h1 className="text-4xl font-bold mb-6 text-btn-primary">Blog</h1>
        <p className="text-lg text-gray-700 mb-10">Expert tips, product updates, and inspiring stories to help you master your money journey.</p>

        {loading && <p className="text-center py-8">Loading posts…</p>}
        {error && <p className="text-center py-8 text-red-500">Error: {error}</p>}

        {posts.length === 0 ? (
          <p className="text-center text-gray-500 py-12">No posts available</p>
        ) : (
          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((post) => {
              const featured = post._embedded?.['wp:featuredmedia']?.[0];
              const image = featured?.source_url || '';
              const excerpt = post.excerpt?.rendered || '';
              const postUrl = post.link || '';

              return (
                <article 
                  key={post.id} 
                  className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden flex flex-col border border-gray-100 cursor-pointer"
                >
                  {image && <img src={image} alt={post.title.rendered} className="h-48 w-full object-cover" />}
                  <div className="p-6 flex flex-col flex-1">
                    <h2 
                      className="text-xl font-semibold mb-2 text-btn-primary line-clamp-2" 
                      dangerouslySetInnerHTML={{__html: post.title.rendered}}
                    />
                    <div
                      className="text-gray-700 mb-4 flex-1 line-clamp-3"
                      dangerouslySetInnerHTML={{__html: excerpt}}
                    />
                    <div className="flex gap-2 mt-auto">
                      <button 
                        onClick={() => {
                          setSelectedPostUrl(postUrl);
                          setSelectedPostTitle(post.title.rendered.replace(/<[^>]*>/g, ''));
                        }}
                        className="flex-1 px-4 py-2 rounded bg-btn-secondary text-white hover:bg-opacity-90 transition-colors font-semibold"
                      >
                        Read More
                      </button>
                      <a 
                        href={postUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="px-4 py-2 rounded bg-btn-primary text-white hover:bg-opacity-90 transition-colors font-semibold"
                        onClick={(e) => e.stopPropagation()}
                      >
                        ↗
                      </a>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
